import type { SearchHit, SearchResultType } from '@fos/shared/contracts/search';
import { SEARCH_MIN_LENGTH } from '@fos/shared/contracts/search';
import { keepPreviousData } from '@tanstack/react-query';
import { Command } from 'cmdk';
import {
  ArrowUpDown,
  BookOpen,
  Bot,
  CalendarDays,
  CornerDownLeft,
  FileText,
  FolderKanban,
  History,
  KeyRound,
  LogOut,
  type LucideIcon,
  Mail,
  PenLine,
  Plus,
  Search,
  Server,
  Sparkles,
  SquareCheckBig,
  UserRound,
  Users,
} from 'lucide-react';
import { Dialog as RadixDialog } from 'radix-ui';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { askAi, useCopilotStatus } from '../../features/copilot';
import { assignAi } from '../../features/tasks';
import { cn } from '../../lib/cn';
import { pushRecent, type RecentItem, readRecent, useRecentKey } from '../../lib/recent';
import { commandFilter } from '../../lib/search';
import { trpc } from '../../lib/trpc';
import { useAccess } from '../../lib/useAccess';
import { Kbd, modKeyLabel } from '../../ui/Kbd';
import { Spinner } from '../../ui/Spinner';
import { useReturnFocus } from '../../ui/useReturnFocus';
import { ADD_ACTIONS, type AddAction, ALL_NAV, CLIENT_CONTEXT_ACTIONS, SETTINGS_NAV } from './nav';
import { useShell } from './shell-context';
import { useSignOut } from './UserMenus';

/** Ikona typu wyniku (Figma 13: klient, projekt, zadanie). */
const HIT_ICONS: Record<SearchResultType, LucideIcon> = {
  client: Users,
  contact: UserRound,
  project: FolderKanban,
  task: SquareCheckBig,
  asset: Server,
  knowledge: BookOpen,
  meeting: CalendarDays,
  offer: FileText,
  message: Mail,
  agent_run: Bot,
};

const SEARCH_DEBOUNCE_MS = 150;

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), ms);
    return () => window.clearTimeout(id);
  }, [value, ms]);
  return debounced;
}

function searchable(q: string): boolean {
  const t = q.trim();
  return t.length >= SEARCH_MIN_LENGTH || /^#?\d+$/.test(t);
}

/**
 * Szukaj i polecenia (⌘K / Ctrl+K), Figma 13 Szukaj (3035:4973):
 *  - bez frazy: ostatnio otwierane (localStorage), akcje „Dodaj…”, nawigacja, ustawienia,
 *  - z frazą: akcje (także „Dodaj czas / Nowe zadanie dla <klient>”, gdy trafiono klienta), wyniki `search.global`
 *    (klienci, kontakty, projekty, zadania, zasoby, wiedza, spotkania, oferty w zakresie osoby), pasujące pozycje menu,
 *  - tryb „Dodaj” (przycisk + w menu): same akcje; każda otwiera formularz modułu w miejscu (AddDialogs).
 * Fala 4: wyniki z wiadomości (temat, nadawca; zakres osoby), „Napisz do: <kontakt>” dla trafionego klienta albo
 * kontaktu (kompozytor poczty → Akceptacje) i grupa „Zapytaj AI” (panel Copilota z wpisaną frazą; bez AI ukryta).
 * Fala 5: wyniki z przebiegów AI (moduł Agenci, „po problemie”) i akcja „Zleć Claude dla zadania…” — wybór zadania
 * w tej samej palecie (wyszukiwanie tylko zadań; Backspace w pustym polu wraca), potem dialog „Zleć Claude”
 * (`assignAi.open`, host w AppShell).
 * Filtrowanie pozycji lokalnych: `commandFilter` (bez polskich znaków); wyniki serwera są już dopasowane.
 */
export function CommandPalette({
  open,
  onOpenChange,
  mode,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'all' | 'add';
}) {
  const navigate = useNavigate();
  const access = useAccess();
  const signOut = useSignOut();
  const { openAddDialog, openComposer } = useShell();
  const recentKey = useRecentKey();
  const [search, setSearch] = useState('');
  /** wybór zadania dla „Zleć Claude dla zadania…” (Fala 5; nazwa z Fali 9) */
  const [picker, setPicker] = useState<'assign-ai' | null>(null);
  /** zaznaczona pozycja (cmdk) wybrana przy danej pierwszej pozycji listy — inna pierwsza pozycja = zaznacz ją */
  const [selection, setSelection] = useState({ base: '', value: '' });
  const onCloseAutoFocus = useReturnFocus(open);
  const mod = modKeyLabel();
  const debounced = useDebounced(search.trim(), SEARCH_DEBOUNCE_MS);

  useEffect(() => {
    if (open) {
      setSearch('');
      setPicker(null);
    }
  }, [open]);
  const pickerMode = mode === 'all' && picker === 'assign-ai';
  const canAssignAi = access.can('agents', 'edit') && access.can('tasks', 'edit');

  const recent = useMemo(() => (open ? readRecent(recentKey) : []), [open, recentKey]);

  const serverEnabled = open && mode === 'all' && searchable(debounced);
  const results = trpc.search.global.useQuery(
    pickerMode ? { q: debounced, limit: 8, types: ['task'] } : { q: debounced, limit: 8 },
    { enabled: serverEnabled, placeholderData: keepPreviousData, staleTime: 15_000 },
  );
  const hits: SearchHit[] = serverEnabled && searchable(search) ? (results.data?.items ?? []) : [];
  const copilot = useCopilotStatus(open && mode === 'all');
  const showAskAi = mode === 'all' && !pickerMode && copilot.data?.enabled === true;
  const searching = serverEnabled && (results.isFetching || debounced !== search.trim());

  const run = (action: () => void) => {
    onOpenChange(false);
    action();
  };
  const runAdd = (action: AddAction, defaults?: { clientId?: string }) =>
    run(() => (action.dialog ? openAddDialog(action.dialog, defaults) : navigate(action.to)));
  const openHit = (hit: { type: SearchResultType; id: string; title: string; subtitle: string; to: string }) =>
    run(() => {
      pushRecent(recentKey, { type: hit.type, id: hit.id, title: hit.title, subtitle: hit.subtitle, to: hit.to });
      void navigate(hit.to);
    });

  const q = search.trim();
  const matches = (value: string, keywords?: string[]) => !q || commandFilter(value, q, keywords) > 0;

  const addActions = ADD_ACTIONS.filter((a) => access.can(a.module, 'edit'));
  const visibleAdd = pickerMode ? [] : addActions.filter((a) => matches(a.label, a.keywords));
  const showAssignAi =
    mode === 'all' &&
    !pickerMode &&
    canAssignAi &&
    matches('Zleć Claude dla zadania', ['ai', 'agent', 'agenci', 'claude', 'runner', 'zlec', 'przebieg']);
  const taskHits = pickerMode ? hits.filter((h) => h.type === 'task') : [];
  const recentTasks = pickerMode && !q ? recent.filter((r) => r.type === 'task') : [];
  const pickTask = (id: string) => run(() => assignAi.open(id));
  // „Dodaj czas dla REA”, „Nowe zadanie dla REA” (Figma 13): akcje kontekstowe dla najlepiej trafionego klienta.
  const topClient = pickerMode ? undefined : hits.find((h) => h.type === 'client');
  const contextual = topClient
    ? CLIENT_CONTEXT_ACTIONS.flatMap((kind) => addActions.filter((a) => a.dialog === kind))
    : [];
  // „Napisz do: Anna Kowalczyk” (Figma 13): kontakt z adresem najlepiej trafionego klienta albo kontaktu
  const writeTo =
    mode === 'all' && !pickerMode && access.can('inbox', 'edit')
      ? hits.find((h) => (h.type === 'client' || h.type === 'contact') && h.contact && h.client)
      : undefined;
  const navItems = pickerMode
    ? []
    : ALL_NAV.filter((item) => item.module === null || access.can(item.module)).filter((item) =>
        matches(`przejdz ${item.label}`),
      );
  const settings = pickerMode ? [] : SETTINGS_NAV.filter((item) => matches(`ustawienia ${item.label}`));
  const showPassword = !pickerMode && matches('ustawienia haslo zmien haslo urzadzenia');
  const showLogout = !pickerMode && matches('wyloguj');

  // Zaznaczona pierwsza pozycja listy (Figma 13), także gdy wyniki z serwera dochodzą po wpisaniu frazy: cmdk zaznacza
  // pierwszą pozycję tylko przy zmianie frazy, więc zaznaczenie zostawało na „Zapytaj AI” i Enter otwierał panel AI
  // zamiast najlepszego trafienia. Kolejność jak w renderze niżej; strzałki działają, dopóki lista się nie zmieni.
  const pickerFirst = recentTasks[0] ?? taskHits[0];
  const firstValue =
    (pickerMode ? (pickerFirst ? `zlec:${pickerFirst.id}` : '') : '') ||
    (mode === 'all' && !pickerMode && !q && recent[0] ? `recent:${recent[0].type}:${recent[0].id}` : '') ||
    (topClient && contextual[0] ? `ctx:${contextual[0].key}` : '') ||
    (writeTo?.contact && writeTo.client ? `napisz:${writeTo.type}:${writeTo.id}` : '') ||
    (visibleAdd[0] ? `akcja:${visibleAdd[0].key}` : '') ||
    (showAssignAi ? 'akcja:zlec-ai' : '') ||
    (mode === 'all' && !pickerMode && hits[0] ? `hit:${hits[0].type}:${hits[0].id}` : '') ||
    (showAskAi ? 'zapytaj-ai' : '') ||
    (mode === 'all' && navItems[0] ? `przejdz:${navItems[0].key}` : '');
  const selectedValue = selection.base === firstValue ? selection.value : firstValue;

  const nothing =
    !pickerMode &&
    !showAssignAi &&
    visibleAdd.length === 0 &&
    contextual.length === 0 &&
    hits.length === 0 &&
    !writeTo &&
    !showAskAi &&
    (mode === 'add' || (navItems.length === 0 && settings.length === 0 && !showPassword && !showLogout)) &&
    !searching;

  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-50 bg-overlay animate-fade-in" />
        <RadixDialog.Content
          aria-describedby={undefined}
          onCloseAutoFocus={onCloseAutoFocus}
          className={cn(
            // md+: środek obszaru treści (obok menu 232 px), jak w Figmie 13
            'fixed top-3 left-1/2 z-50 w-[calc(100vw-24px)] max-w-[640px] -translate-x-1/2 overflow-hidden md:top-[120px] md:left-[calc(50%+116px)] md:w-[calc(100vw-232px-48px)]',
            'rounded-2xl bg-canvas shadow-modal animate-pop-in focus:outline-none',
          )}
        >
          <RadixDialog.Title className="sr-only">Szukaj i polecenia</RadixDialog.Title>
          <Command
            shouldFilter={false}
            loop
            label="Szukaj i polecenia"
            value={selectedValue}
            onValueChange={(value) => setSelection({ base: firstValue, value })}
          >
            <div className="flex h-14 items-center gap-3 border-b border-line px-[18px]">
              <Search size={18} strokeWidth={1.75} aria-hidden className="shrink-0 text-fg-muted" />
              <Command.Input
                value={search}
                onValueChange={setSearch}
                placeholder={
                  pickerMode
                    ? 'Zleć Claude — wpisz tytuł albo #numer zadania'
                    : mode === 'add'
                      ? 'Co chcesz dodać?'
                      : 'Szukaj albo wpisz polecenie…'
                }
                onKeyDown={(e) => {
                  // Backspace w pustym polu wraca z wyboru zadania do zwykłej palety
                  if (pickerMode && e.key === 'Backspace' && search === '') {
                    e.preventDefault();
                    setPicker(null);
                  }
                }}
                className="h-full min-w-0 flex-1 bg-transparent text-body-m text-fg outline-none placeholder:text-fg-muted"
              />
              {searching && <Spinner size={14} className="text-fg-muted" />}
              <Kbd>Esc</Kbd>
            </div>
            <Command.List className="max-h-[min(440px,60dvh)] overflow-y-auto px-2 py-2">
              {nothing && (
                <div className="px-3 py-6 text-center text-body-s text-fg-muted" role="status">
                  Nic nie znaleziono.
                </div>
              )}

              {pickerMode && (
                <Command.Group heading="Zleć Claude dla zadania" className={groupClasses}>
                  {(q ? taskHits : recentTasks).map((t) => (
                    <PaletteItem
                      key={`zlec:${t.id}`}
                      value={`zlec:${t.id}`}
                      icon={SquareCheckBig}
                      label={t.title}
                      hint={t.subtitle}
                      onSelect={() => pickTask(t.id)}
                    />
                  ))}
                  {(q ? taskHits : recentTasks).length === 0 && !searching && (
                    <div className="px-3 py-4 text-body-s text-fg-muted" role="status">
                      {q ? 'Nie znaleziono zadania.' : 'Wpisz tytuł albo #numer zadania.'}
                    </div>
                  )}
                </Command.Group>
              )}

              {mode === 'all' && !pickerMode && !q && recent.length > 0 && (
                <Command.Group heading="Ostatnio otwierane" className={groupClasses}>
                  {recent.map((r: RecentItem) => (
                    <PaletteItem
                      key={`recent:${r.type}:${r.id}`}
                      value={`recent:${r.type}:${r.id}`}
                      icon={HIT_ICONS[r.type] ?? History}
                      label={r.title}
                      hint={r.subtitle}
                      onSelect={() => openHit(r)}
                    />
                  ))}
                </Command.Group>
              )}

              {(contextual.length > 0 || visibleAdd.length > 0 || writeTo || showAssignAi) && (
                <Command.Group heading="Akcje" className={groupClasses}>
                  {topClient &&
                    contextual.map((action) => (
                      <PaletteItem
                        key={`ctx:${action.key}`}
                        value={`ctx:${action.key}`}
                        icon={action.icon}
                        label={`${action.label} dla ${topClient.title}`}
                        onSelect={() => runAdd(action, { clientId: topClient.id })}
                      />
                    ))}
                  {writeTo?.contact && writeTo.client && (
                    <PaletteItem
                      value={`napisz:${writeTo.type}:${writeTo.id}`}
                      icon={PenLine}
                      label={`Napisz do: ${writeTo.contact.name}`}
                      hint={writeTo.client.name}
                      onSelect={() =>
                        run(() =>
                          openComposer({
                            clientId: writeTo.client?.id as string,
                            to: [{ email: writeTo.contact?.email as string, name: writeTo.contact?.name }],
                          }),
                        )
                      }
                    />
                  )}
                  {visibleAdd.map((action) => (
                    <PaletteItem
                      key={action.key}
                      value={`akcja:${action.key}`}
                      icon={action.icon ?? Plus}
                      label={action.label}
                      onSelect={() => runAdd(action)}
                    />
                  ))}
                  {showAssignAi && (
                    <PaletteItem
                      value="akcja:zlec-ai"
                      icon={Sparkles}
                      label="Zleć Claude dla zadania…"
                      onSelect={() => {
                        setPicker('assign-ai');
                        setSearch('');
                      }}
                    />
                  )}
                </Command.Group>
              )}

              {mode === 'all' && !pickerMode && hits.length > 0 && (
                <Command.Group heading="Wyniki" className={groupClasses}>
                  {hits.map((hit) => (
                    <PaletteItem
                      key={`hit:${hit.type}:${hit.id}`}
                      value={`hit:${hit.type}:${hit.id}`}
                      icon={HIT_ICONS[hit.type]}
                      label={hit.title}
                      hint={hit.subtitle}
                      onSelect={() => openHit(hit)}
                    />
                  ))}
                </Command.Group>
              )}

              {showAskAi && (
                <Command.Group heading="Zapytaj AI" className={groupClasses}>
                  <PaletteItem
                    value="zapytaj-ai"
                    icon={Sparkles}
                    label={q || 'Zapytaj AI…'}
                    hint={q ? 'zapytaj AI' : undefined}
                    onSelect={() => run(() => askAi.open(q ? { draft: q } : {}))}
                  />
                </Command.Group>
              )}

              {mode === 'all' && navItems.length > 0 && (
                <Command.Group heading="Przejdź do" className={groupClasses}>
                  {navItems.map((item) => (
                    <PaletteItem
                      key={item.key}
                      value={`przejdz:${item.key}`}
                      icon={item.icon}
                      label={item.label}
                      hint="moduł"
                      onSelect={() => run(() => navigate(item.to))}
                    />
                  ))}
                </Command.Group>
              )}

              {mode === 'all' && (settings.length > 0 || showPassword || showLogout) && (
                <Command.Group heading="Ustawienia" className={groupClasses}>
                  {settings.map((item) => (
                    <PaletteItem
                      key={item.to}
                      value={`ustawienia:${item.to}`}
                      icon={item.icon}
                      label={item.label}
                      hint="ustawienia"
                      onSelect={() => run(() => navigate(item.to))}
                    />
                  ))}
                  {showPassword && (
                    <PaletteItem
                      value="ustawienia:haslo"
                      icon={KeyRound}
                      label="Zmień hasło"
                      hint="bezpieczeństwo"
                      onSelect={() => run(() => navigate('/ustawienia/bezpieczenstwo'))}
                    />
                  )}
                  {showLogout && (
                    <PaletteItem value="wyloguj" icon={LogOut} label="Wyloguj" onSelect={() => run(signOut)} />
                  )}
                </Command.Group>
              )}
            </Command.List>
            <div className="flex h-10 items-center gap-5 bg-surface px-[18px] text-caption text-fg-muted">
              <span className="inline-flex items-center gap-2">
                <Kbd>
                  <ArrowUpDown size={10} strokeWidth={2} aria-hidden />
                </Kbd>
                wybierz
              </span>
              <span className="inline-flex items-center gap-2">
                <Kbd>
                  <CornerDownLeft size={10} strokeWidth={2} aria-hidden />
                </Kbd>
                otwórz
              </span>
              <span className="inline-flex items-center gap-2">
                <Kbd>{mod} K</Kbd>
                zamknij
              </span>
            </div>
          </Command>
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

const groupClasses =
  '[&_[cmdk-group-heading]]:px-2.5 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:pb-1.5 [&_[cmdk-group-heading]]:text-caption [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-fg-muted';

function PaletteItem({
  value,
  icon: Icon,
  label,
  hint,
  onSelect,
}: {
  value: string;
  icon: LucideIcon;
  label: ReactNode;
  hint?: string;
  onSelect: () => void;
}) {
  return (
    <Command.Item
      value={value}
      onSelect={onSelect}
      className="group flex h-10 cursor-pointer items-center gap-3 rounded-lg px-2.5 text-body-m text-fg data-[selected=true]:bg-elevated"
    >
      <Icon size={18} strokeWidth={1.75} aria-hidden className="shrink-0 text-fg-secondary" />
      <span className="truncate">{label}</span>
      {hint && <span className="truncate text-caption text-fg-muted">{hint}</span>}
      <span className="flex-1" />
      <Kbd className="invisible group-data-[selected=true]:visible">
        <CornerDownLeft size={10} strokeWidth={2} aria-hidden />
      </Kbd>
    </Command.Item>
  );
}
